import { useEffect, useState } from 'react';
import Layout from './Layout';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Typography,
} from '@mui/material';

function ChangePage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [changes, setChanges] = useState(null);
    const [sortColumn, setSortColumn] = useState('bill'); // Default sort column
    const [sortOrder, setSortOrder] = useState('asc'); // Default sort order
    const userId = localStorage.getItem('user_id');

    useEffect(() => {
        async function fetchChanges() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/bill-change?user_id=${userId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
                    },
                });
                const data = await response.json();
                setChanges(data);
            } catch (error) {
                console.error('Error fetching changes:', error);
            }
        }

        fetchChanges();
    }, [userId]);

    const handleSort = (column) => {
        const isAsc = sortColumn === column && sortOrder === 'asc';
        setSortColumn(column);
        setSortOrder(isAsc ? 'desc' : 'asc');
    };

    const handleRowClick = async (changeId, currentReadStatus) => {
        try {
            // Toggle the `read` field in the backend
            const updatedReadStatus = !currentReadStatus;
            await fetch(`${process.env.REACT_APP_API_URL}/bill-change/${changeId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
                },
                body: JSON.stringify({ read: updatedReadStatus }),
            });

            // Update the `read` field locally
            setChanges((prevChanges) =>
                prevChanges.map((change) =>
                    change.id === changeId ? { ...change, read: updatedReadStatus } : change
                )
            );
        } catch (error) {
            console.error('Error updating read status:', error);
        }
    };

    const columnValue = (change, column) => {
        switch (column) {
            case 'bill':
                return `${change.bill.state} - ${change.bill.bill_number}`;
            case 'description':
                return change.bill.description || '';
            case 'change':
                return change.change_text || '';
            case 'date':
                return new Date(change.created_at);
            default:
                return '';
        }
    };

    const sortedChanges = changes
        ? [...changes].sort((a, b) => {
              const aValue = columnValue(a, sortColumn);
              const bValue = columnValue(b, sortColumn);

              if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
              if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
              return 0;
          })
        : [];

    return (
        <Layout searchQuery={searchQuery}>
            <Box sx={{ width: '100%' }}>
                {changes ? (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="sortable table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortColumn === 'bill'}
                                            direction={sortColumn === 'bill' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('bill')}
                                        >
                                            Bill
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortColumn === 'description'}
                                            direction={sortColumn === 'description' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('description')}
                                        >
                                            Bill Description
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortColumn === 'change'}
                                            direction={sortColumn === 'change' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('change')}
                                        >
                                            Change
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortColumn === 'date'}
                                            direction={sortColumn === 'date' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('date')}
                                        >
                                            Date
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedChanges.map((change) => (
                                    <TableRow
                                        key={change.id}
                                        onClick={() => handleRowClick(change.id, change.read)}
                                        sx={{
                                            backgroundColor: change.read ? 'white' : '#f0f0f0',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <TableCell>
                                            <a
                                                href={`/bill/${change.bill.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(event) => event.stopPropagation()} // Prevent row click
                                            >
                                                {change.bill.state} - {change.bill.bill_number} ({change.bill.year_introduced})
                                            </a>
                                        </TableCell>
                                        <TableCell>{change.bill.description}</TableCell>
                                        <TableCell>{change.change_text}</TableCell>
                                        <TableCell>
                                            {new Date(change.created_at).toLocaleDateString('en-US')}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>Loading changes...</Typography>
                )}
            </Box>
        </Layout>
    );
}

export default ChangePage;